/**
 * `/admin/expert-payments` — the internal accounts-payable console.
 *
 * The screen that stands between a confirmed hour and an expert being paid.
 * Accrual creates a `pending` obligation and never calls Stripe; money moves
 * only when a human here clicks. Every other link in that chain already exists.
 *
 * **Platform-wide, not engagements.** Rows come from consultations, written
 * responses, response referrals and engagement hours submissions, and the first
 * real use is the consultation payout run Finance does by hand today. A layout
 * shaped around engagements would fail the job.
 *
 * **Flat and single-purpose.** Three stacked sections, no tab shell: the
 * settlement queue, the manual rail, and the tax ledger. Whether an
 * accounts-RECEIVABLE queue joins this console is still an open product
 * question, and it is planned as a sibling route rather than as a tab here — so
 * the answer costs a new route, not a re-layout.
 *
 * Not built on `src/routes/payoutDashboard/`: that is the expert-facing view of
 * an expert's own payouts. Different audience, different gate, different data.
 */
import { useRef, useState } from 'react';

import Button from '@/componentsv2/Button';
import Dialog from '@/componentsv2/Dialog';

import LedgerView from './ledger/LedgerView';
import RecordManualPaymentForm from './manual/RecordManualPaymentForm';
import PaymentQueue, { type PaymentQueueHandle } from './queue/PaymentQueue';

export default function ExpertPaymentsConsole(): JSX.Element {
  const [recording, setRecording] = useState(false);
  // Called after a manual record so the queue re-reads: the new row lands in
  // `paid`, and a stale queue would hide a payment that now exists.
  //
  // A ref rather than a bumped `key`: remounting would get the same fresh read
  // and lose the queue's execution-freeze latch, its open status tab, and every
  // page already paged in. Recording a manual payment is exactly what an
  // operator does DURING a freeze, so the latch is live precisely when this
  // fires.
  const queue = useRef<PaymentQueueHandle>(null);

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-32 px-16 py-24 sm:px-24">
      <header className="flex flex-wrap items-start justify-between gap-16 border-b border-surface-border pb-16">
        <div className="flex flex-col gap-4">
          <h1 className="text-on-surface hd-3">Expert payments</h1>
          <p className="text-on-surface-muted body-2">
            Release what the platform owes experts, record what was paid off platform, and read the
            year’s totals. Payments cannot be edited, reversed or clawed back here — or anywhere.
          </p>
        </div>
        <Button onClick={() => setRecording(true)}>Record a manual payment</Button>
      </header>

      <PaymentQueue ref={queue} />

      <LedgerView />

      {/* disablePortal={false}, as the settlement dialog and every other
          repo caller does: `componentsv2/Dialog` defaults it on, and MUI then
          aria-hides body's children other than the modal — which, rendered in
          place, is nested inside one of them. The dialog ends up under an
          aria-hidden ancestor and is unreachable to assistive tech. */}
      <Dialog
        open={recording}
        onClose={() => setRecording(false)}
        dialogTitle="Record a payment made off platform"
        showCloseButton
        disablePortal={false}
      >
        <RecordManualPaymentForm
          onClose={() => setRecording(false)}
          onRecorded={() => queue.current?.reload()}
        />
      </Dialog>
    </div>
  );
}
